import React, { useEffect, useRef, useState } from "react";
import { getFreshToken } from "./auth.js";
import { BTN, ui, type Lang, type LandingTab, type TechPackEntry } from "./shared.js";
import { PackFolderList, type FolderEntry } from "./pack_folder_list.js";
import { notifyDenied } from "./access_denial_toast.js";

// Server row from GET /api/studio/import/specflow/sources (snake_case, no content).
interface SpecflowSource {
  id: string;
  style_number: string | null;
  status: string;
  current_version_id: string | null;
  updated_at: string;
  linked_studio_pack_id: string | null;
  already_imported: boolean;
}

interface FileListProps {
  packs: TechPackEntry[];
  initialFolders: FolderEntry[];
  onSelect: (pack: TechPackEntry) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  onImport: (entry: TechPackEntry) => void;
  lang: Lang;
  // Folder-service Activity (P2a-3/V1): report the shared folder the user is
  // working in, so the landing ChatPanel shows that folder's activity stream.
  onActiveFolderChange?: (folderId: string | null) => void;
  activeFolderId?: string | null;          // highlight + scope the Surface Activity
}

const EXPAND_KEY = "studio_folder_expanded";

export function FileList({ packs, initialFolders, onSelect, onNew, onDelete, onImport, lang, onActiveFolderChange, activeFolderId }: FileListProps) {
  const [tab,            setTab]            = useState<LandingTab>(() => {
    const saved = localStorage.getItem("studio_active_tab");
    return saved === "shared" || saved === "mine" ? saved : "mine";
  });
  // Seeded from App's pre-fetched folders so the first render already has the
  // folder set — no flash of packs at root. FileList owns this state after mount;
  // all folder CRUD below mutates it locally.
  const [folders,        setFolders]        = useState<FolderEntry[]>(() => initialFolders);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [folderDraft,    setFolderDraft]    = useState("");
  const importRef      = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const folderEditRef  = useRef<HTMLInputElement>(null);

  const [editingFolderId,   setEditingFolderId]   = useState<string|null>(null);
  const [editingFolderName, setEditingFolderName] = useState("");
  const [folderError,       setFolderError]       = useState<string|null>(null);

  // SpecFlow import chooser (item 12): inline panel under the header listing
  // the caller's SpecFlow roots + the retained .json file path.
  const [importOpen,    setImportOpen]    = useState(false);
  const [importSources, setImportSources] = useState<SpecflowSource[]|null>(null);
  const [importError,   setImportError]   = useState<string|null>(null);
  const [importBusy,    setImportBusy]    = useState(false);

  // packId -> folderId. Server (packs.folderId) is authoritative; localStorage is a
  // pre-migration fallback only (removed once migrated to DB on first load).
  const [packFolders, setPackFolders] = useState<Record<string,string>>(() => {
    const fromPacks = Object.fromEntries(packs.flatMap(p => p.folderId ? [[p.id, p.folderId]] : []));
    try { return { ...JSON.parse(localStorage.getItem("studio_pack_folders") ?? "{}") as Record<string,string>, ...fromPacks }; }
    catch { return fromPacks; }
  });

  // folderId -> expanded. Pure UI state, persisted to localStorage (not DB).
  const [expanded, setExpanded] = useState<Record<string,boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(EXPAND_KEY) ?? "{}") as Record<string,boolean>; }
    catch { return {}; }
  });

  // Keep packFolders in sync when the server-backed packs list changes.
  useEffect(() => {
    setPackFolders(prev => {
      const next = { ...prev };
      for (const p of packs) { if (p.folderId) next[p.id] = p.folderId; }
      return next;
    });
  }, [packs]);

  // RC3: share-toggles in flight, folderId -> desired is_shared. The E-3 sync
  // effect below must not clobber an optimistic toggle with stale refetch data:
  // clicking 🌐 in an unfocused window fires App's focus-refetch FIRST, whose
  // GET can read the DB before our PATCH commits — without this guard the
  // wholesale replace bounced the folder back to "shared" and the unshare
  // looked like it needed two attempts (the first actually committed; UI lied).
  const pendingShareRef = useRef<Map<string, boolean>>(new Map());

  // E-3: re-sync folders when App re-fetches (initialFolders prop changes).
  // `folders` is seeded once by the useState initializer above and FileList
  // re-mounts on every editor enter/exit — without this, a refreshLists() on
  // goBack/refocus updates App's initialFolders but FileList never picks it up,
  // so a refetched (e.g. newly-shared) folder wouldn't render. Server is the
  // source of truth for the folder set — replace wholesale, EXCEPT a folder
  // whose share-toggle is still in flight (RC3): hold its optimistic is_shared
  // until the server catches up, then release the guard.
  useEffect(() => {
    const pend = pendingShareRef.current;
    setFolders(initialFolders.map(f => {
      if (!pend.has(f.id)) return f;
      if (f.is_shared === pend.get(f.id)) { pend.delete(f.id); return f; }  // server caught up
      return { ...f, is_shared: pend.get(f.id)! };                          // stale → hold optimistic
    }));
  }, [initialFolders]);

  // Folders are pre-fetched by App and seeded into state above — no GET here.
  // This mount effect only handles the one-time localStorage → DB migration for
  // installs that predate F-1/F-2a, then drops the legacy keys.
  useEffect(() => {
    getFreshToken().then(async token => {
      if (!token) return;
      try {
        const idMap: Record<string,string> = {};
        if (initialFolders.length === 0) {
          // No server folders yet: migrate any localStorage folders to DB.
          const local: FolderEntry[] = (() => {
            try { return JSON.parse(localStorage.getItem("studio_folders") ?? "[]") as FolderEntry[]; }
            catch { return []; }
          })();
          if (local.length > 0) {
            const migrated: FolderEntry[] = [];
            for (const lf of local) {
              const cr = await fetch("/api/studio/folders", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ name: lf.name }),
              });
              if (cr.ok) { const c = await cr.json() as FolderEntry; migrated.push(c); idMap[lf.id] = c.id; }
            }
            setFolders(migrated);
          }
        }
        localStorage.removeItem("studio_folders");
        // Migrate studio_pack_folders localStorage → DB (one-time), then drop the key.
        const pfRaw = localStorage.getItem("studio_pack_folders");
        if (pfRaw) {
          const lp: Record<string,string> = (() => { try { return JSON.parse(pfRaw) as Record<string,string>; } catch { return {}; } })();
          localStorage.removeItem("studio_pack_folders");
          const resolved: Record<string,string> = {};
          for (const [pid, fid] of Object.entries(lp)) {
            const target = idMap[fid] ?? fid;
            resolved[pid] = target;
            try { await fetch(`/api/studio/techpacks/${pid}/folder`, { method:"PATCH", headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`}, body:JSON.stringify({ folder_id: target }) }); }
            catch { /**/ }
          }
          setPackFolders(prev => ({ ...prev, ...resolved }));
        }
      } catch { /* network error — keep seeded folders */ }
    });
  }, []);

  useEffect(() => { if (creatingFolder) folderInputRef.current?.focus(); }, [creatingFolder]);
  useEffect(() => { if (editingFolderId) folderEditRef.current?.focus(); }, [editingFolderId]);
  // Persist the active tab on EVERY change (tab buttons + the +FOLDER → "mine"
  // jump), so PARTAGÉ survives refresh. Single source of truth — no missed writes.
  useEffect(() => { try { localStorage.setItem("studio_active_tab", tab); } catch { /**/ } }, [tab]);

  function persistExpanded(next: Record<string,boolean>) {
    setExpanded(next);
    try { localStorage.setItem(EXPAND_KEY, JSON.stringify(next)); } catch { /**/ }
  }
  function toggleExpand(folderId: string) {
    persistExpanded({ ...expanded, [folderId]: !expanded[folderId] });
    // Working in a shared folder focuses its folder-level Activity stream.
    if (tab === "shared") onActiveFolderChange?.(folderId);
  }

  function dupFolderLabel(): string {
    return lang === "zh" ? "已存在同名文件夹" : lang === "fr" ? "Un dossier de ce nom existe déjà" : lang === "it" ? "Esiste già una cartella con questo nome" : "A folder with this name already exists";
  }

  // showError=true (Enter key): keep input open and show error on duplicate.
  // showError=false (blur): close silently even on duplicate (no infinite-blur loop).
  async function saveFolder(showError = false) {
    const name = folderDraft.trim();
    if (!name) { setCreatingFolder(false); setFolderDraft(""); setFolderError(null); return; }
    if (folders.some(f => f.name === name)) {
      if (showError) { setFolderError(dupFolderLabel()); }
      else { setCreatingFolder(false); setFolderDraft(""); setFolderError(null); }
      return;
    }
    setCreatingFolder(false);
    setFolderDraft("");
    setFolderError(null);
    const token = await getFreshToken();
    if (!token) return;
    try {
      const r = await fetch("/api/studio/folders", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      if (r.ok) { const entry = await r.json() as FolderEntry; setFolders(prev => [...prev, entry]); }
      else if (r.status === 409) { setCreatingFolder(true); setFolderDraft(name); setFolderError(dupFolderLabel()); }
    } catch { /* ignore */ }
  }

  async function removeFolder(id: string) {
    const removed = folders.find(f => f.id === id);
    // Packs in this folder fall back to root locally; server uses ON DELETE SET NULL.
    setFolders(prev => prev.filter(f => f.id !== id));
    setPackFolders(prev => {
      const next = { ...prev };
      for (const [pid, fid] of Object.entries(next)) { if (fid === id) delete next[pid]; }
      return next;
    });
    const token = await getFreshToken();
    if (!token) return;
    const res = await fetch(`/api/studio/folders/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }).catch(() => null);
    if (res && res.ok) return;
    // UX-COPY-1: a collaborator's delete returns 403 — restore the optimistically
    // removed folder (don't let it vanish-then-reappear) and explain why.
    if (removed) setFolders(prev => prev.some(f => f.id === id) ? prev : [...prev, removed]);
    if (res && res.status === 403) notifyDenied("denial_delete_owner_only");
  }

  async function saveRenameFolder() {
    const id = editingFolderId;
    if (!id) return;
    const name = editingFolderName.trim();
    const current = folders.find(f => f.id === id);
    const finalName = name || current?.name || "";
    setEditingFolderId(null);
    setEditingFolderName("");
    if (!finalName || finalName === current?.name) return;
    if (folders.some(f => f.id !== id && f.name === finalName)) return; // duplicate guard
    setFolders(prev => prev.map(f => f.id === id ? { ...f, name: finalName } : f));
    const token = await getFreshToken();
    if (!token) return;
    try {
      await fetch(`/api/studio/folders/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: finalName }),
      });
    } catch { /* ignore */ }
  }

  // Move pack in / out / between folders — single server endpoint, optimistic update.
  async function assignToFolder(packId: string, folderId: string|null) {
    setPackFolders(prev => {
      const next = { ...prev };
      if (folderId) next[packId] = folderId; else delete next[packId];
      return next;
    });
    const token = await getFreshToken();
    if (!token) return;
    try { await fetch(`/api/studio/techpacks/${packId}/folder`, { method:"PATCH", headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`}, body:JSON.stringify({ folder_id: folderId }) }); }
    catch { /* ignore */ }
  }

  // Move a folder between private and shared areas (owner-only) — optimistic +
  // persist. RC3: register the toggle as pending BEFORE the async token gap so
  // a focus-refetch landing mid-flight can't bounce the UI back; reconcile from
  // the PATCH response (the endpoint RETURNINGs the updated row) and release.
  async function assignFolderShared(folderId: string, next: boolean) {
    pendingShareRef.current.set(folderId, next);
    setFolders(prev => prev.map(f => f.id === folderId ? { ...f, is_shared: next } : f));
    const token = await getFreshToken();
    if (!token) { pendingShareRef.current.delete(folderId); return; }
    try {
      const res = await fetch(`/api/studio/folders/${folderId}`, { method:"PATCH", headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`}, body:JSON.stringify({ is_shared: next }) });
      pendingShareRef.current.delete(folderId);
      if (res.ok) {
        const row = await res.json() as FolderEntry;
        setFolders(prev => prev.map(f => f.id === folderId ? { ...f, ...row } : f));
      } else {
        // PATCH refused (403/404/409) — revert the optimistic flip to server truth.
        setFolders(prev => prev.map(f => f.id === folderId ? { ...f, is_shared: !next } : f));
      }
    } catch {
      // Network blip: outcome unknown — release the guard and let the next
      // refetch reconcile to whatever actually committed.
      pendingShareRef.current.delete(folderId);
    }
  }

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // File-import path: POST the full canonical document; the server validates,
    // maps, dedupes and binds it. A non-JSON file is an explicit inline error —
    // no state change.
    file.text().then(text => {
      let document: unknown;
      try { document = JSON.parse(text); }
      catch { setImportError(ui("import_invalid_file", lang)); return; }
      void postImport({ document });
    }).catch(() => setImportError(ui("import_invalid_file", lang)));
    e.target.value = "";
  }

  async function openImportChooser() {
    if (importOpen) { setImportOpen(false); return; }
    setImportOpen(true);
    setImportError(null);
    setImportSources(null);
    const token = await getFreshToken();
    if (!token) return;
    try {
      const res = await fetch("/api/studio/import/specflow/sources", { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setImportSources(await res.json() as SpecflowSource[]);
      else setImportError(`${res.status}`);
    } catch { setImportError("network"); }
  }

  async function postImport(body: Record<string, unknown>) {
    if (importBusy) return;
    setImportBusy(true);
    setImportError(null);
    const token = await getFreshToken();
    if (!token) { setImportBusy(false); return; }
    try {
      const res = await fetch("/api/studio/import/specflow", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({})) as TechPackEntry & { error?: string };
      if (res.ok) {
        setImportOpen(false);
        onImport(data);
      } else if (data.error === "unknown_specflow_pack") {
        setImportError(ui("import_unknown_pack", lang));
      } else if (data.error === "invalid_canonical_document" || data.error === "invalid_import_request") {
        setImportError(ui("import_invalid_file", lang));
      } else {
        // Honest fallback: surface the server code verbatim rather than guessing.
        setImportError(data.error ?? `${res.status}`);
      }
    } catch { setImportError("network"); }
    finally { setImportBusy(false); }
  }

  // Per-tab split: shared/private is a folder-level property; packs inherit it.
  const sharedView = tab === "shared";
  const visibleFolders = folders.filter(f => !!f.is_shared === sharedView);
  // Unfiled packs (no folder, or folder deleted) are always private → mine tab only.
  const folderIds = new Set(folders.map(f => f.id));
  const unfiledPacks = packs.filter(p => { const fid = packFolders[p.id]; return !fid || !folderIds.has(fid); });
  const rootPacks = sharedView ? [] : unfiledPacks;
  const tabEmpty = visibleFolders.length === 0 && rootPacks.length === 0 && !(creatingFolder && !sharedView);

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden", background:"#0a0a0a" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 20px", borderBottom:"1px solid var(--border)", flexShrink:0, height:44 }}>
        <div style={{ display:"flex" }}>
          {([{ id:"mine" as LandingTab, key:"my_styles" },{ id:"shared" as LandingTab, key:"shared" }]).map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ fontFamily:"'Rama Gothic M', system-ui", background:"transparent", border:"none", borderBottom: tab===t.id ? "2px solid var(--text)" : "2px solid transparent", color: tab===t.id ? "var(--text)" : "var(--text-muted)", fontSize:"0.6rem", letterSpacing:"0.15em", padding:"0 12px", cursor:"pointer", textTransform:"uppercase", height:44 }}>{ui(t.key, lang)}</button>
          ))}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <input ref={importRef} type="file" accept=".json" style={{ display:"none" }} onChange={handleImportFile} />
          <button onClick={() => void openImportChooser()} style={BTN}>IMPORT</button>
          {/* Folders are created only in My Styles (private), then shared via the
              per-folder toggle — hidden in the Shared tab. */}
          {!sharedView && <button onClick={() => setCreatingFolder(true)} style={BTN}>+ FOLDER</button>}
          <button onClick={onNew} style={{ ...BTN, color:"var(--text)", borderColor:"#5a5a5a" }}>{ui("new_pack", lang)}</button>
        </div>
      </div>
      <div style={{ flex:1, overflowY:"auto" }}>
        {importOpen && (
          <div style={{ borderBottom:"1px solid #161616", background:"#0d0d0d", padding:"8px 20px 10px" }}>
            <div style={{ fontFamily:"'Space Mono', monospace", fontSize:9, color:"#666", letterSpacing:"0.08em", marginBottom:6 }}>{ui("import_specflow", lang).toUpperCase()}</div>
            {importSources === null && !importError && (
              <div style={{ fontFamily:"'Space Mono', monospace", fontSize:10, color:"#555", padding:"2px 0" }}>…</div>
            )}
            {importSources?.map(s => (
              <button key={s.id} disabled={importBusy} onClick={() => void postImport({ techpack_id: s.id })}
                style={{ display:"flex", alignItems:"center", gap:8, width:"100%", background:"transparent", border:"none", borderBottom:"1px solid #111", padding:"6px 0", cursor: importBusy ? "default" : "pointer", textAlign:"left" }}>
                <span style={{ flex:1, fontFamily:"'Space Mono', monospace", fontSize:11, color:"var(--text)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {s.style_number || `(unbound) …${s.id.slice(-6)}`}
                </span>
                <span style={{ fontFamily:"'Space Mono', monospace", fontSize:9, color:"#666", letterSpacing:"0.08em", flexShrink:0 }}>{s.status.toUpperCase()}</span>
                {s.already_imported && (
                  <span style={{ fontFamily:"'Space Mono', monospace", fontSize:9, color:"#e0a055", flexShrink:0 }}>{ui("import_already", lang)}</span>
                )}
              </button>
            ))}
            <button disabled={importBusy} onClick={() => importRef.current?.click()}
              style={{ display:"block", width:"100%", background:"transparent", border:"none", padding:"8px 0 2px", cursor: importBusy ? "default" : "pointer", textAlign:"left", fontFamily:"'Space Mono', monospace", fontSize:10, color:"#9a9a9a", letterSpacing:"0.08em" }}>
              {ui("import_from_file", lang).toUpperCase()}
            </button>
            {importError && (
              <div style={{ paddingTop:6, fontFamily:"'Space Mono', monospace", fontSize:10, color:"#e05555" }}>{importError}</div>
            )}
          </div>
        )}
        {creatingFolder && !sharedView && (
          <div style={{ borderBottom:"1px solid #161616", background:"#0d0d0d" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, padding:"10px 20px" }}>
              <span style={{ fontSize:13 }}>📁</span>
              <input
                ref={folderInputRef}
                type="text"
                value={folderDraft}
                onChange={e => { setFolderDraft(e.target.value); setFolderError(null); }}
                onKeyDown={e => { if (e.key==="Enter") { void saveFolder(true); } if (e.key==="Escape") { setCreatingFolder(false); setFolderDraft(""); setFolderError(null); } }}
                onBlur={() => { void saveFolder(false); }}
                placeholder={lang === "zh" ? "文件夹名称…" : "Folder name…"}
                style={{ fontFamily:"'Space Mono', monospace", fontSize:11, background:"transparent", border:"none", borderBottom:`1px solid ${folderError ? "#e05555" : "#3a3a3a"}`, color:"var(--text)", outline:"none", flex:1, padding:"2px 0" }}
              />
            </div>
            {folderError && (
              <div style={{ padding:"0 20px 8px 39px", fontFamily:"'Space Mono', monospace", fontSize:10, color:"#e05555" }}>{folderError}</div>
            )}
          </div>
        )}
        {tabEmpty ? (
          <div style={{ textAlign:"center", padding:"60px 20px", fontFamily:"'Space Mono', monospace", fontSize:11, color:"var(--text-muted)" }}>{ui(sharedView ? "empty_shared" : "empty_mine", lang)}</div>
        ) : (
          <PackFolderList
            lang={lang}
            folders={visibleFolders}
            packs={packs}
            rootPacks={rootPacks}
            packFolders={packFolders}
            expanded={expanded}
            editingFolderId={editingFolderId}
            editingFolderName={editingFolderName}
            folderEditRef={folderEditRef}
            onToggleExpand={toggleExpand}
            onSelect={onSelect}
            onDeletePack={onDelete}
            onMove={assignToFolder}
            onStartRename={(id, name) => { setEditingFolderId(id); setEditingFolderName(name); }}
            onRenameChange={setEditingFolderName}
            onCommitRename={saveRenameFolder}
            onCancelRename={() => { setEditingFolderId(null); setEditingFolderName(""); }}
            onRemoveFolder={removeFolder}
            onToggleShared={assignFolderShared}
            activeFolderId={activeFolderId}
            onActiveFolderChange={onActiveFolderChange}
          />
        )}
      </div>
    </div>
  );
}
